import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge, readinessTone, EmptyState } from "@/components/Primitives";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck } from "lucide-react";

export default function Readiness() {
  const { departments, selectedDept, setSelectedDept, settings } = useApp();
  const [deptId, setDeptId] = useState("");
  const [data, setData] = useState(null);
  const threshold = settings?.low_performer_threshold || 60;

  useEffect(() => {
    const initial = selectedDept !== "all" ? selectedDept : departments[0]?.id;
    if (initial) setDeptId(initial);
  }, [departments, selectedDept]);

  const load = useCallback(() => {
    if (!deptId) return;
    api.get("/readiness", { params: { department_id: deptId } }).then((r) => setData(r.data)).catch(() => {});
  }, [deptId]);

  useEffect(() => { load(); }, [load]);

  const mark = async (memberId, criterionId, field, value) => {
    const member = data.members.find((m) => m.member.id === memberId);
    const item = member.items.find((i) => i.criterion_id === criterionId);
    await api.post("/readiness/mark", {
      member_id: memberId,
      criterion_id: criterionId,
      trained: field === "trained" ? value : item.trained,
      assessed: field === "assessed" ? value : item.assessed,
      score: item.score,
    });
    load();
  };

  return (
    <div>
      <PageHeader
        title="Department Readiness"
        subtitle="Configurable SOP compliance checklist per department member."
        action={
          <Select value={deptId} onValueChange={(v) => { setDeptId(v); setSelectedDept(v); }}>
            <SelectTrigger data-testid="readiness-dept-select" className="w-[200px] border-slate-700"><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        }
      />

      {!data || data.criteria.length === 0 ? (
        <Card><EmptyState icon={ShieldCheck} title="No active criteria" hint="Add training criteria for this department in Settings." /></Card>
      ) : data.members.length === 0 ? (
        <Card><EmptyState icon={ShieldCheck} title="No members" hint="Add team members to this department first." /></Card>
      ) : (
        <div className="space-y-4">
          {data.members.map((row) => (
            <Card key={row.member.id} className="p-5" data-testid={`readiness-card-${row.member.id}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <p className="text-white font-medium">{row.member.name}</p>
                  <p className="text-slate-500 text-xs font-mono">{row.member.employee_id} · {row.member.role}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-2xl font-light" style={{ color: row.readiness_pct >= 90 ? "#059669" : row.readiness_pct < threshold ? "#DC2626" : "#C9A24B" }}>
                    {row.readiness_pct}%
                  </span>
                  <Badge tone={readinessTone(row.readiness_pct, threshold)}>
                    {row.readiness_pct >= 90 ? "SOP Ready" : row.readiness_pct < threshold ? "Below Standard" : "Improving"}
                  </Badge>
                </div>
              </div>
              <div className="border-t border-slate-800">
                {row.items.map((item) => (
                  <div key={item.criterion_id} className={`flex items-center justify-between py-2.5 border-b border-slate-800/60 ${item.trained ? "opacity-90" : ""}`}>
                    <span className={`text-sm ${item.trained ? "text-slate-400 line-through" : "text-slate-200"}`}>{item.name}</span>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                        <Checkbox checked={item.trained} onCheckedChange={(v) => mark(row.member.id, item.criterion_id, "trained", !!v)} data-testid={`trained-${row.member.id}-${item.criterion_id}`} />
                        Trained
                      </label>
                      <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                        <Checkbox checked={item.assessed} onCheckedChange={(v) => mark(row.member.id, item.criterion_id, "assessed", !!v)} data-testid={`assessed-${row.member.id}-${item.criterion_id}`} />
                        Assessed
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
